import { Button } from '@/shared/components/ui/Button';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Activity, Check, Copy, Edit2, Loader2, MailPlus, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { MessageEventEditor } from './MessageEventEditor';
import { MessageEventExecutionsPage } from './MessageEventExecutionsPage';
import {
  type MessageEvent,
  buildTriggerUrl,
  deleteMessageEvent,
  listMessageEvents,
  updateMessageEvent,
} from './api';

const PAGE_SIZE = 50;

export function MessageEventsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MessageEvent | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MessageEvent | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [historyEventId, setHistoryEventId] = useState<number | null>(null);

  const listQ = useInfiniteQuery({
    queryKey: ['message-events'],
    queryFn: ({ pageParam }) =>
      listMessageEvents({
        limit: PAGE_SIZE,
        ...(typeof pageParam === 'number' && { cursor: pageParam }),
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });

  const events = useMemo<MessageEvent[]>(
    () => listQ.data?.pages.flatMap((p) => p.items) ?? [],
    [listQ.data],
  );

  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !listQ.hasNextPage || listQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) void listQ.fetchNextPage();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listQ.hasNextPage, listQ.isFetchingNextPage, listQ.fetchNextPage]);

  // Mede primeira linha + header pra setar maxHeight = 10 linhas visíveis,
  // sem chutar pixels.
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState<number | null>(null);
  useEffect(() => {
    const t = tableRef.current;
    if (!t || events.length === 0) return;
    const headerRow = t.querySelector('thead tr') as HTMLElement | null;
    const firstBodyRow = t.querySelector('tbody tr') as HTMLElement | null;
    if (!headerRow || !firstBodyRow) return;
    setScrollMaxHeight(headerRow.offsetHeight + firstBodyRow.offsetHeight * 10);
  }, [events.length]);

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      updateMessageEvent(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['message-events'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteMessageEvent(id),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['message-events'] });
      toast.success('Evento removido');
    },
  });

  const copyTrigger = async (event: MessageEvent) => {
    const url = buildTriggerUrl(event.trigger_path, event.trigger_url);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(event.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error('Falha ao copiar URL');
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="font-bold text-[20px] text-n-900" style={{ letterSpacing: '-.02em' }}>
          Push
        </h1>
        <Button onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" />
          Novo push
        </Button>
      </header>

      <p className="text-[12px] text-n-500">
        Configure pushes que sistemas externos disparam (POST com JSON) pra enviar mensagens
        WhatsApp pros seus leads e aplicar ação no atendimento.
      </p>

      <div
        className="overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm"
        style={scrollMaxHeight ? { maxHeight: scrollMaxHeight } : undefined}
      >
        <table ref={tableRef} className="w-full text-[13px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Nome
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Webhook
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Status
              </th>
              <th className="px-[14px] py-[10px] text-right font-bold text-[11px] uppercase text-n-500">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center">
                  <Loader2 className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!listQ.isLoading && events.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-12 text-center text-n-500">
                  Nenhum push configurado. Clique em "Novo push" pra começar.
                </td>
              </tr>
            )}
            {events.map((e) => (
              <tr key={e.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px]">
                  <div className="flex items-center gap-2">
                    <MailPlus size={14} className="text-brand-500" />
                    <span className="font-semibold text-n-900">{e.name}</span>
                  </div>
                </td>
                <td className="px-[14px] py-[11px]">
                  <button
                    type="button"
                    onClick={() => copyTrigger(e)}
                    className="flex items-center gap-1.5 font-mono text-[11px] text-n-600 hover:text-brand-600"
                    title="Copiar URL"
                  >
                    {copiedId === e.id ? (
                      <Check size={12} className="text-success-500" />
                    ) : (
                      <Copy size={12} />
                    )}
                    <span className="truncate">
                      {buildTriggerUrl(e.trigger_path, e.trigger_url)}
                    </span>
                  </button>
                </td>
                <td className="px-[14px] py-[11px]">
                  <button
                    type="button"
                    onClick={() => toggleMut.mutate({ id: e.id, active: !e.active })}
                    className={`relative h-[20px] w-[34px] cursor-pointer rounded-pill ${
                      e.active ? 'bg-success-500' : 'bg-n-300'
                    }`}
                    aria-label={e.active ? 'Desativar' : 'Ativar'}
                  >
                    <span
                      className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-n-0 shadow ${
                        e.active ? 'right-[2px]' : 'left-[2px]'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => setHistoryEventId(e.id)}
                      className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
                      title="Histórico de execuções"
                    >
                      <Activity size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(e)}
                      className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
                      title="Editar"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(e)}
                      className="flex h-8 w-8 items-center justify-center rounded text-danger-500 hover:bg-danger-50"
                      title="Deletar"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {listQ.hasNextPage && (
              <tr ref={sentinelRef}>
                <td colSpan={4} className="py-4 text-center text-[12px] text-n-500">
                  {listQ.isFetchingNextPage ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Carregando mais…
                    </span>
                  ) : (
                    'Role para carregar mais'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && <MessageEventEditor onClose={() => setCreating(false)} />}
      {editing && <MessageEventEditor event={editing} onClose={() => setEditing(null)} />}

      {historyEventId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setHistoryEventId(null);
          }}
        >
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-y-auto rounded-md bg-n-0 p-6 shadow-xl">
            <MessageEventExecutionsPage
              eventId={historyEventId}
              onClose={() => setHistoryEventId(null)}
            />
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDelete(null);
          }}
        >
          <div className="w-full max-w-md rounded-md bg-n-0 p-6 shadow-xl">
            <h3 className="font-bold text-[16px] text-n-900">Apagar evento?</h3>
            <p className="mt-2 text-[13px] text-n-600">
              &quot;{confirmDelete.name}&quot; será removido. Histórico de execuções será apagado em
              cascata.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="rounded border border-n-200 bg-n-0 px-3 py-2 font-semibold text-[13px]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => deleteMut.mutate(confirmDelete.id)}
                className="rounded bg-danger-500 px-3 py-2 font-semibold text-[13px] text-white"
              >
                Apagar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
